import React, { useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { McInput, McButton, McIcon } from "@maersk-global/mds-react-wrapper/components-core";
import "./chainedFiveWhys.css";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold", "italic", "underline",
  "list", "bullet",
  "link",
];

function ChainContent({ chain, onUpdateChain }) {
  const handleLabelChange = useCallback((e) => {
    onUpdateChain(chain.chainId, { chainLabel: e.target.value });
  }, [chain.chainId, onUpdateChain]);

  const handleDescriptionChange = useCallback((e) => {
    onUpdateChain(chain.chainId, { chainDescription: e.target.value });
  }, [chain.chainId, onUpdateChain]);

  const handleWhyQuestionChange = useCallback((whyNumber, value) => {
    const currentWhy = chain.whys.find(why => why.whyNumber === whyNumber);
    if (currentWhy && currentWhy.question === value) return;
    const updatedWhys = chain.whys.map(why =>
      why.whyNumber === whyNumber ? { ...why, question: value } : why
    );
    onUpdateChain(chain.chainId, { whys: updatedWhys });
  }, [chain.chainId, chain.whys, onUpdateChain]);

  const handleWhyAnswerChange = useCallback((whyNumber, value) => {
    const currentWhy = chain.whys.find(why => why.whyNumber === whyNumber);
    if (currentWhy && currentWhy.answer === value) return;
    const updatedWhys = chain.whys.map(why =>
      why.whyNumber === whyNumber ? { ...why, answer: value } : why
    );
    onUpdateChain(chain.chainId, { whys: updatedWhys });
  }, [chain.chainId, chain.whys, onUpdateChain]);

  const handleAddWhy = useCallback(() => {
    const newWhyNumber = chain.whys.length + 1;
    onUpdateChain(chain.chainId, {
      whys: [...chain.whys, { whyNumber: newWhyNumber, question: '', answer: '' }],
    });
  }, [chain.chainId, chain.whys, onUpdateChain]);

  const handleDeleteWhy = useCallback((whyNumber) => {
    if (chain.whys.length <= 1) return;
    const updatedWhys = chain.whys
      .filter(why => why.whyNumber !== whyNumber)
      .map((why, index) => ({ ...why, whyNumber: index + 1 }));
    onUpdateChain(chain.chainId, { whys: updatedWhys });
  }, [chain.chainId, chain.whys, onUpdateChain]);

  const filledWhys = chain.whys.filter(why => why.question?.trim() && why.answer?.trim());
  const totalWhys = chain.whys.length;
  const isComplete = filledWhys.length >= 5;
  const hasStarted = chain.whys.some(why => why.question?.trim() || why.answer?.trim());

  return (
    <div className="chain-content">
      {/* Chain Label */}
      <div className="chain-field">
        <McInput
          label="Chain Label"
          value={chain.chainLabel || ''}
          placeholder="e.g., P2 Root cause, Escalation cause"
          fit="medium"
          onInput={handleLabelChange}
        />
        <div className="chain-label-preview">
          <span className="preview-text">
            Will appear as: <strong>Chain {chain.chainLetter}{chain.chainLabel ? ` — ${chain.chainLabel}` : ''}</strong>
          </span>
        </div>
      </div>

      {/* Chain Description */}
      <div className="chain-field">
        <McInput
          label="Chain Description (optional)"
          value={chain.chainDescription || ''}
          placeholder="Brief description shown below chain title"
          fit="medium"
          onInput={handleDescriptionChange}
        />
      </div>

      {/* Progress / status banner */}
      {isComplete ? (
        <div style={{
          padding: "8px 12px", marginBottom: "16px",
          background: "rgba(64,171,53,0.1)", border: "1px solid rgba(64,171,53,0.35)",
          borderRadius: "var(--mds_brand_border_medium_radius, 4px)",
          display: "flex", alignItems: "center", gap: "8px",
        }}>
          <McIcon icon="check-circle" size="16" style={{ color: "#40ab35", flexShrink: 0 }} />
          <span style={{ fontSize: "13px", color: "#2d7a25", fontWeight: "600" }}>
            Chain complete — {filledWhys.length} Whys filled
          </span>
          {totalWhys > 5 && (
            <span style={{ fontSize: "12px", color: "#6b6b6b", marginLeft: "auto" }}>
              {totalWhys} whys total (5 minimum required)
            </span>
          )}
        </div>
      ) : hasStarted ? (
        <div style={{
          padding: "8px 12px", marginBottom: "16px",
          background: "var(--mds_brand_appearance_warning_weakest_background-color, #fff7e6)",
          border: "1px solid var(--mds_brand_appearance_warning_default_border-color, #f59e0b)",
          borderRadius: "var(--mds_brand_border_medium_radius, 4px)",
          display: "flex", alignItems: "center", gap: "8px",
        }}>
          <McIcon icon="info-circle" size="16" style={{ color: "#f59e0b", flexShrink: 0 }} />
          <span style={{ fontSize: "13px", color: "#92400e", fontWeight: "600" }}>
            {filledWhys.length}/5 Whys filled — keep going until you reach the root cause
          </span>
        </div>
      ) : (
        <div style={{
          padding: "8px 12px", marginBottom: "16px",
          background: "var(--mds_brand_appearance_neutral_weakest_background-color, #f8f9fa)",
          border: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)",
          borderRadius: "var(--mds_brand_border_medium_radius, 4px)",
          display: "flex", alignItems: "center", gap: "8px",
        }}>
          <McIcon icon="info-circle" size="16" style={{ color: "var(--mds_brand_appearance_primary_default_text-color, #0073ab)", flexShrink: 0 }} />
          <span style={{ fontSize: "13px", color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)" }}>
            Fill in at least <strong>5 Whys</strong> — ask "Why?" until you reach a systemic root cause
          </span>
        </div>
      )}

      {/* 5 Whys */}
      <div className="chain-whys">
        {chain.whys.map((why) => (
          <div key={why.whyNumber} className="why-item">
            {/* WHY header strip */}
            <div className="why-header">
              <span style={{
                fontSize: "11px", fontWeight: "700", letterSpacing: "0.06em",
                color: "var(--mds_brand_appearance_primary_default_text-color, #0073ab)",
              }}>
                WHY {why.whyNumber}
              </span>
              {chain.whys.length > 1 && (
                <button
                  onClick={() => handleDeleteWhy(why.whyNumber)}
                  className="why-delete-btn"
                  title="Delete this why"
                  style={{
                    marginLeft: "auto", border: "none", background: "transparent",
                    cursor: "pointer", padding: "4px", display: "flex", alignItems: "center",
                    color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "var(--mds_brand_appearance_error_default_text-color, #c0392b)"}
                  onMouseLeave={(e) => e.currentTarget.style.color = "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)"}
                >
                  <McIcon icon="trash" size="16" />
                </button>
              )}
            </div>

            {/* Question */}
            <div className="why-field">
              <McInput
                label="Question"
                value={why.question || ''}
                placeholder={`Why ${why.whyNumber === 1 ? 'did this happen' : 'is that'}?`}
                fit="medium"
                onInput={(e) => handleWhyQuestionChange(why.whyNumber, e.target.value)}
              />
            </div>

            {/* Answer */}
            <div className="why-field">
              <label className="why-field-label">Answer (Because…)</label>
              <div className="quill-wrapper">
                <ReactQuill
                  theme="snow"
                  value={why.answer || ''}
                  onChange={(html) => handleWhyAnswerChange(why.whyNumber, html)}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Because..."
                  style={{ background: "#fff" }}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Add Why */}
        <div style={{ marginTop: "12px" }}>
          <McButton
            appearance="neutral"
            variant="outlined"
            fit="small"
            trailingicon="plus"
            click={handleAddWhy}
          >
            Add Why {chain.whys.length + 1}
          </McButton>
        </div>
      </div>
    </div>
  );
}

export default ChainContent;
